// HiPages offers — one screen: what the watcher saw, EmmA's Yes/No call on each, and what a job
// like it has been worth. Read-only on purpose: an offer is not a lead, and auto-accept is not armed
// (Jacky, 29 September 2026: "I am still not ready to auto accept"). Every row starts collapsed; the
// brief, her reason and the revenue basis open on demand.
import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Button,cn} from '@aiw/ui';
import type {EmmaCall,HipageOffer,HipageOffersView} from '@aiw/api-client';
import {Page} from '../shell/Page';
import {authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {aud} from '../lib/format';
import {useT} from '../lib/i18n';

type Filter='all'|'yes'|'no'|'pending';
const DAYS=[7,30,90] as const;

async function readOffers(days:number):Promise<HipageOffersView>{
  const r=await fetchImpl(`${env.apiBase}/api/hipage-offers?days=${days}`,{headers:await authHeaders()});
  const body=await r.json().catch(()=>null) as {data?:HipageOffersView;error?:string}|null;
  if(!r.ok||!body?.data)throw new Error(body?.error??'HiPages offers unavailable');
  return body.data;
}

const TONE:Record<EmmaCall,string>={
  yes:'bg-emerald-100 text-emerald-900 border-emerald-300',
  no:'bg-stone-100 text-stone-700 border-stone-300',
  unsure:'bg-amber-100 text-amber-900 border-amber-300',
  pending:'bg-muted text-muted-foreground border-border',
};
const MARK:Record<EmmaCall,string>={yes:'✓',no:'✗',unsure:'?',pending:'…'};

export function HipageOffers(){
  const {t,lang}=useT();
  const [days,setDays]=useState<number>(7);
  const [filter,setFilter]=useState<Filter>('all');
  const q=useQuery({queryKey:['hipage-offers',days],queryFn:()=>readOffers(days),refetchInterval:60_000});
  const locale=lang==='zh'?'zh-CN':'en-AU';
  const seen=(iso:string)=>new Date(iso).toLocaleString(locale,{timeZone:'Australia/Sydney',weekday:'short',day:'numeric',month:'short',hour:'numeric',minute:'2-digit'});
  const gst=(n:number|null)=>n===null?'—':t('hipage.plusGst',{amount:aud(n)});
  const took=(s:number)=>s<60?t('hipage.reaction.seconds',{n:s<10?s.toFixed(1):Math.round(s)}):s<3600?t('hipage.reaction.minutes',{n:Math.round(s/60)}):s<172800?t('hipage.reaction.hours',{n:Math.round(s/3600)}):t('hipage.reaction.days',{n:Math.round(s/86400)});
  const callLabel=(c:EmmaCall)=>t(c==='yes'?'hipage.emmaYes':c==='no'?'hipage.emmaNo':c==='unsure'?'hipage.emmaUnsure':'hipage.emmaPending');
  const view=q.data;
  const rows=(view?.offers??[]).filter(o=>filter==='all'||(filter==='pending'?o.emma==='pending'||o.emma==='unsure':o.emma===filter));

  return <Page title={t('hipage.title')} lede={t('hipage.lede')}
    actions={<span className="rounded-full border px-3 py-1 text-sm">{t('hipage.autoAcceptOff')}</span>}>
    <div className="mb-4 flex flex-wrap gap-2">
      {DAYS.map(d=><Button key={d} variant={d===days?'default':'outline'} onClick={()=>setDays(d)}>{t('hipage.window',{days:d})}</Button>)}
    </div>

    {q.isError?<p role="alert" className="text-sm">{t('hipage.loadError')}</p>:q.isPending?<p className="text-sm text-muted-foreground">{t('hipage.loading')}</p>:view&&<>
      <section className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <Tile label={t('hipage.offers')} value={String(view.totals.offers)}/>
        <Tile label={t('hipage.emmaYes')} value={String(view.totals.yes)} tone="yes"/>
        <Tile label={t('hipage.emmaNo')} value={String(view.totals.no)}/>
        <Tile label={t('hipage.emmaPending')} value={String(view.totals.pending+view.totals.unsure)}/>
        <Tile label={t('hipage.potentialYes')} value={gst(view.totals.potential_yes)} tone="yes"/>
        <Tile label={t('hipage.reaction.median')} value={view.totals.reaction_median_s===null?'—':took(view.totals.reaction_median_s)}
          note={view.totals.reaction_late?t('hipage.reaction.lateCount',{n:view.totals.reaction_late}):undefined}/>
      </section>

      <div className="mb-3 flex flex-wrap gap-2" role="tablist">
        {(['all','yes','no','pending'] as Filter[]).map(f=><Button key={f} role="tab" aria-selected={f===filter} variant={f===filter?'default':'outline'} onClick={()=>setFilter(f)}>{t(`hipage.filter.${f}` as 'hipage.filter.all')}</Button>)}
      </div>

      {rows.length===0?<p className="text-sm text-muted-foreground">{t('hipage.empty')}</p>:
      <ul className="space-y-2">{rows.map(o=><OfferRow key={o.id} o={o} seen={seen(o.seen_at)} potential={gst(o.potential_value)} reaction={o.emma_reaction_s===null?null:o.emma_late?t('hipage.reaction.caughtUp',{time:took(o.emma_reaction_s)}):took(o.emma_reaction_s)} callLabel={callLabel} t={t}/>)}</ul>}
    </>}
  </Page>;
}

function Tile({label,value,tone,note}:{label:string;value:string;tone?:'yes';note?:string}){
  return <div className={cn('rounded-lg border bg-card p-3',tone==='yes'&&'border-emerald-300')}>
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    {note&&<div className="mt-1 text-sm text-muted-foreground">{note}</div>}
  </div>;
}

function OfferRow({o,seen,potential,reaction,callLabel,t}:{o:HipageOffer;seen:string;potential:string;reaction:string|null;callLabel:(c:EmmaCall)=>string;t:ReturnType<typeof useT>['t']}){
  return <li>
    <details className="rounded-lg border bg-card">
      <summary className="flex cursor-pointer flex-wrap items-center gap-3 p-3">
        <span className={cn('inline-flex min-w-28 items-center gap-1 rounded-full border px-2 py-0.5 text-sm font-medium',TONE[o.emma])} aria-label={callLabel(o.emma)}>
          <span aria-hidden>{MARK[o.emma]}</span>{callLabel(o.emma)}
        </span>
        {reaction&&<span className={cn('text-sm tabular-nums',o.emma_late?'text-muted-foreground':'')} title={t('hipage.reaction.title')}>⏱ {reaction}</span>}
        <span className="font-medium">{o.trade??'—'}</span>
        <span className="text-sm text-muted-foreground">{[o.suburb,o.postcode].filter(Boolean).join(' ')||'—'}</span>
        {o.credits!==null&&<span className="text-sm text-muted-foreground">{t('hipage.credits',{n:o.credits})}</span>}
        <span className="text-sm text-muted-foreground">{seen}</span>
        <span className="ml-auto text-sm"><span className="text-muted-foreground">{t('hipage.potential')} </span><strong className="tabular-nums">{potential}</strong></span>
      </summary>
      <dl className="grid gap-x-6 gap-y-2 border-t p-3 text-sm sm:grid-cols-[10rem_1fr]">
        <dt className="text-muted-foreground">{t('hipage.detail.brief')}</dt><dd>{o.description||t('hipage.noBrief')}</dd>
        <dt className="text-muted-foreground">{t('hipage.detail.timing')}</dt><dd>{o.timing??'—'}</dd>
        <dt className="text-muted-foreground">{t('hipage.detail.action')}</dt><dd>{o.action??'—'}</dd>
        <dt className="text-muted-foreground">{t('hipage.detail.emmaReason')}</dt>
        <dd>{o.emma==='pending'?t('hipage.detail.noReason'):<>{o.emma_reason??'—'}{o.emma_confidence!==null&&<span className="text-muted-foreground"> · {t('hipage.detail.confidence',{pct:Math.round(o.emma_confidence*100)})}</span>}</>}</dd>
        <dt className="text-muted-foreground">{t('hipage.reaction.title')}</dt>
        <dd>{reaction??t('hipage.detail.noReason')}</dd>
        <dt className="text-muted-foreground">{t('hipage.detail.yourRuling')}</dt>
        <dd>{o.verdict?<>{t(o.verdict==='yes'?'hipage.ruling.yes':o.verdict==='no'?'hipage.ruling.no':'hipage.ruling.unsure')}{o.verdict_reason&&<span className="text-muted-foreground"> · {o.verdict_reason}</span>}</>:t('hipage.detail.noRuling')}</dd>
        <dt className="text-muted-foreground">{t('hipage.potential')}</dt>
        <dd>{potential} · <span className="text-muted-foreground">{t(o.potential_basis==='trade'?'hipage.basis.trade':'hipage.basis.all',{n:o.potential_basis_jobs})}</span></dd>
      </dl>
    </details>
  </li>;
}
